import { Link } from 'react-router-dom';
import { CalendarBlank, CaretRight, Star } from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { paths } from '../../routes/paths';
import { cn } from '../../utils/cn';
import { formatEUR } from '../../utils/money';
import { allocationDotClass } from '../../utils/quickCreateAllocation';
import { formatGoalTargetMonth, isChecklistGoal } from '../../utils/goalDisplayUtils';
import { GoalAllocationTag } from './GoalAllocationTag';
import { GoalVisionImage, VISION_SCRIM_CLASS } from './GoalVisionImage';

const TONE_BAND = 'bg-gradient-to-r from-sage-500 to-sage-600';

/** ds/card/goal-vision-hero no .pen — objetivo prioritário do vision board. */
export function PriorityGoalCard({ goal, className }) {
  const isChecklist = isChecklistGoal(goal);
  const target = Number(goal.target_amount || 0);
  const accumulated = Number(goal.accumulated || 0);
  const remaining = Math.max(target - accumulated, 0);
  const pct = Math.min(100, Math.max(0, Number(goal.progress_pct || 0)));
  const completedCount = goal.checklist_completed_count ?? 0;
  const totalCount = goal.checklist_total_count ?? 0;
  const monthlyPlan = Number(goal.monthly_required || goal.monthly_amount || 0);
  const targetMonth = formatGoalTargetMonth(goal.target_date);
  const remainingSteps = Math.max(totalCount - completedCount, 0);

  return (
    <Link
      to={paths.goal(goal.id)}
      className={cn(
        'block overflow-hidden rounded-ds-xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md',
        className,
      )}
    >
      <div className="relative flex h-[200px] flex-col justify-between gap-space-3 p-space-5">
        <GoalVisionImage
          imageUrl={goal.image_url}
          alt={goal.name}
          groupClassName={allocationDotClass(goal.budget_allocation)}
          className="absolute inset-0"
        />
        <div className={VISION_SCRIM_CLASS} />

        <div className="relative flex items-center justify-between gap-space-2">
          <span className="inline-flex items-center gap-space-1 rounded-ds-full bg-on-hero-track px-space-2-5 py-space-1 type-chip text-on-hero">
            <Star className="h-3 w-3" weight="bold" aria-hidden />
            {pt.goals.vision.priorityBadge}
          </span>
          <GoalAllocationTag allocation={goal.budget_allocation} />
        </div>

        <div className="relative flex flex-col gap-space-2">
          <h2 className="type-title-page-lg text-on-hero">{goal.name}</h2>
          {goal.why ? (
            <p className="type-body-sm text-on-hero-muted">{goal.why}</p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-space-3 p-space-5">
        <div className="flex items-end justify-between gap-space-4">
          <div className="flex flex-col gap-0.5">
            <p className="type-section text-muted-foreground">{pt.goals.vision.remainingLabel}</p>
            <p className="type-display-kpi text-card-foreground">
              {isChecklist
                ? pt.goals.vision.remainingAmountPassos.replace('{count}', String(remainingSteps))
                : formatEUR(remaining)}
            </p>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <p className="type-display-stat text-sage-on-tint">{Math.round(pct)} %</p>
            <p className="type-chip text-muted-foreground">{pt.goals.vision.completedLabel}</p>
          </div>
        </div>

        <div className="h-2 w-full overflow-hidden rounded-full bg-track">
          <div className={cn('h-full rounded-full', TONE_BAND)} style={{ width: `${pct}%` }} />
        </div>

        <div className="flex items-center justify-between gap-space-3">
          <p className="type-label text-muted-foreground">
            {isChecklist
              ? pt.goals.vision.checklistSteps
                  .replace('{done}', String(completedCount))
                  .replace('{total}', String(totalCount))
              : `${formatEUR(accumulated)} de ${formatEUR(target)}`}
          </p>
          {!isChecklist && monthlyPlan > 0 ? (
            <p className="type-label font-semibold text-sage-on-tint">
              {pt.goals.vision.monthlyToSave.replace('{amount}', formatEUR(monthlyPlan))}
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-space-3 border-t border-border pt-space-3">
          {targetMonth ? (
            <span className="inline-flex items-center gap-space-1 rounded-ds-full bg-sage-tint px-space-2-5 py-space-1 type-chip text-sage-on-tint">
              <CalendarBlank className="h-3 w-3" aria-hidden />
              {pt.goals.vision.targetMonthPill.replace('{date}', targetMonth)}
            </span>
          ) : (
            <span />
          )}
          <span className="inline-flex items-center gap-space-1 type-label font-semibold text-sage-on-tint">
            {pt.goals.vision.viewGoalLink}
            <CaretRight className="h-3.5 w-3.5" weight="bold" aria-hidden />
          </span>
        </div>
      </div>
    </Link>
  );
}
